import { radiusCva, Text, useProvider } from "@overmap-ai/blocks"
import * as RadixRadioGroup from "@radix-ui/react-radio-group"
import { cx } from "class-variance-authority"
import React, { memo, useCallback } from "react"
import { twMerge } from "tailwind-merge"

import { MapStyle } from "../../mapbox/enums"
import { mapStyleDataMapping } from "../../mapbox/mappings"
import { setMapStyle, useAppDispatch, useAppSelector } from "../../store"
import { selectMapStyle } from "../../store/selectors"

interface MapThumbnailProps {
	isSelected: boolean
	mapStyle: MapStyle
}

const MapThumbnail = memo<MapThumbnailProps>(function MapThumbnail(props) {
	const { isSelected, mapStyle } = props
	const { src, defaultStyle, selectedStyle, label } = mapStyleDataMapping[mapStyle]
	const { radius } = useProvider()

	return (
		<RadixRadioGroup.Item className="group flex flex-col gap-1" data-accent-color="primary" value={mapStyle}>
			<div
				className={twMerge(
					cx(
						"aspect-1 size-15 group-data-[state='checked']:outline-(--primary-indicator) bg-contain bg-center bg-no-repeat shadow-md group-data-[state='checked']:outline-2 group-data-[state='checked']:-outline-offset-1",
						radiusCva({ radius, maxLarge: true }),
					),
				)}
				style={{ background: `url(${src})`, ...(isSelected ? selectedStyle || {} : defaultStyle || {}) }}
			/>
			<Text accentColor={!isSelected ? "base" : undefined} align="center" size="xs">
				{label}
			</Text>
		</RadixRadioGroup.Item>
	)
})
MapThumbnail.displayName = "MapThumbnail"

export const MapControl = memo(function MapControl() {
	const dispatch = useAppDispatch()
	const selectedMapStyle = useAppSelector(selectMapStyle)

	const handleMapThumbnailClick = useCallback(
		(style: MapStyle) => {
			dispatch(setMapStyle(style))
		},
		[dispatch],
	)

	return (
		<RadixRadioGroup.Root
			className="grid w-max grid-cols-4 gap-2"
			value={selectedMapStyle}
			onValueChange={handleMapThumbnailClick}
		>
			{Object.keys(mapStyleDataMapping).map((mapStyle) => {
				const selected = selectedMapStyle === mapStyle
				return <MapThumbnail mapStyle={mapStyle as MapStyle} key={mapStyle} isSelected={selected} />
			})}
		</RadixRadioGroup.Root>
	)
})
MapControl.displayName = "MapControl"
